import { Block, BlockTitle, List, ListItem, Navbar, Page, f7 } from 'framework7-react';
import type { OrderTypeConfig, OrderTypeConfigBody } from '@restaurant/api-client';
import { featureFlags } from '@/featureFlags';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useT } from '@/i18n';
import {
  useReservationConfig,
  useUpdateReservationConfig,
} from '@/features/reservation-setup/hooks/useReservationConfig';
import {
  useOrderTypeConfig,
  useUpdateOrderTypeConfig,
} from '@/features/order-setup/hooks/useOrderSetup';

/**
 * Notification opt-out settings. For online orders the owner controls emails /
 * alerts along TWO independent axes: ORDER TYPE (pickup / delivery) and PAYMENT
 * METHOD (cash / bank transfer / card-online). A notification fires only when
 * the order type AND the payment method are both on - mirrors the backend
 * `customerEmailAllowed` gate (customerEmailEnabled AND the per-kind flag). The
 * payment axis is shared across both order types, so each payment toggle writes
 * the PICKUP and DELIVERY configs together. Reservations have no payment method,
 * so they keep plain master switches. Dine-in orders never notify.
 */

type OrderBoolField = {
  [K in keyof OrderTypeConfig]: OrderTypeConfig[K] extends boolean ? K : never;
}[keyof OrderTypeConfig] &
  keyof OrderTypeConfigBody;

function MailSettingsPage() {
  const t = useT();
  const backLink = useSettingsDetailBackLink();

  const { data: resConfig } = useReservationConfig();
  const updateRes = useUpdateReservationConfig();
  const { data: orderConfigs } = useOrderTypeConfig();
  const updateOrder = useUpdateOrderTypeConfig();

  // Each flip commits immediately and confirms with a bottom toast (desktop:
  // bottom-right corner) - the switch position IS the state, no Save button.
  // Mirrors ReservationSetupPage. Reused for every mutation via `notify`.
  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });
  const notify = {
    onSuccess: () => toast(t.saved),
    onError: (e: unknown) => toast(e instanceof Error ? e.message : t.error),
  };

  // Plain controlled checkbox with F7's `.toggle` markup instead of the F7
  // <Toggle> component: in this mutation-driven, re-rendering settings list the
  // <Toggle> instance's `toggleChange` event sometimes never wires, so taps did
  // nothing. Here React owns the checkbox - onChange always follows the tap.
  // (Mirrors ReservationSetupPage / OpeningHoursPage.)
  const toggleCtl = (opts: {
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <label className={`toggle${opts.disabled ? ' disabled' : ''}`} slot="after">
      <input
        type="checkbox"
        checked={opts.checked}
        disabled={opts.disabled}
        onChange={(e) => opts.onChange(e.target.checked)}
      />
      <span className="toggle-icon" />
    </label>
  );

  // --- Online-order notification toggles (two axes: order type × payment) ---
  // A restaurant that has never edited its order settings has no OrderTypeConfig
  // rows yet - the row is BORN on the first write (updateOrderConfig upserts with
  // all-true defaults). So the toggles must stay live when a row is merely absent
  // and reflect the schema defaults; only the still-loading query greys them out.
  // (`orderConfigs` is undefined while loading, then an array - possibly empty.)
  const pickup = orderConfigs?.find((c) => c.orderType === 'PICKUP');
  const delivery = orderConfigs?.find((c) => c.orderType === 'DELIVERY');
  const ordersLoading = orderConfigs === undefined;

  // Order-type axis: write one order type's flag.
  const setType = (type: 'PICKUP' | 'DELIVERY', data: OrderTypeConfigBody) =>
    updateOrder.mutate({ orderType: type, data }, notify);
  // Payment axis is shared across order types → write PICKUP + DELIVERY together
  // (toast once, on the second).
  const setBoth = (data: OrderTypeConfigBody) => {
    updateOrder.mutate({ orderType: 'PICKUP', data });
    updateOrder.mutate({ orderType: 'DELIVERY', data }, notify);
  };

  // ORDER-TYPE row: pickup / delivery gate (customerEmailEnabled / ownerNotifyEnabled
  // / ownerEmailEnabled). `disabledWhen` optionally greys the row (owner-email
  // rides on the app alert, so it is disabled when owner-notify is off).
  const typeRow = (
    type: 'PICKUP' | 'DELIVERY',
    field: OrderBoolField,
    label: string,
    disabledWhen?: (cfg: OrderTypeConfig | undefined) => boolean,
  ) => {
    const cfg = type === 'PICKUP' ? pickup : delivery;
    return (
      <ListItem title={label}>
        {toggleCtl({
          checked: cfg?.[field] ?? true,
          disabled: ordersLoading || (disabledWhen ? disabledWhen(cfg) : false),
          onChange: (v) => setType(type, { [field]: v } as OrderTypeConfigBody),
        })}
      </ListItem>
    );
  };

  // PAYMENT-METHOD row: shared across both order types. On only when both are on.
  const payRow = (field: OrderBoolField, label: string) => (
    <ListItem title={label}>
      {toggleCtl({
        checked: (pickup?.[field] ?? true) && (delivery?.[field] ?? true),
        disabled: ordersLoading,
        onChange: (v) => setBoth({ [field]: v } as OrderTypeConfigBody),
      })}
    </ListItem>
  );

  // "All payment methods" convenience - flips the three kind flags together.
  const allPayRow = (
    cash: OrderBoolField,
    manual: OrderBoolField,
    online: OrderBoolField,
    label: string,
  ) => {
    const on = ([cash, manual, online] as OrderBoolField[]).every(
      (f) => (pickup?.[f] ?? true) && (delivery?.[f] ?? true),
    );
    return (
      <ListItem title={label}>
        {toggleCtl({
          checked: on,
          disabled: ordersLoading,
          onChange: (v) => setBoth({ [cash]: v, [manual]: v, [online]: v } as OrderTypeConfigBody),
        })}
      </ListItem>
    );
  };

  const m = t.settings.mail;

  return (
    <Page name="mail-settings">
      <Navbar title={m.title} backLink={backLink} />

      <BlockTitle>{m.reservationTitle}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={m.reservationCustomer}>
          {toggleCtl({
            checked: resConfig?.customerEmailEnabled ?? true,
            disabled: !resConfig,
            onChange: (v) => updateRes.mutate({ customerEmailEnabled: v }, notify),
          })}
        </ListItem>
        {featureFlags.ownerEmailNotifications && (
          <ListItem title={m.reservationOwnerEmail}>
            {toggleCtl({
              checked: resConfig?.ownerEmailEnabled ?? true,
              // Email rides on the app alert - only meaningful when owner notify is on.
              disabled: !resConfig || !resConfig.ownerNotifyEnabled,
              onChange: (v) => updateRes.mutate({ ownerEmailEnabled: v }, notify),
            })}
          </ListItem>
        )}
        <ListItem title={m.reservationReminder}>
          {toggleCtl({
            checked: resConfig?.reminderEnabled ?? true,
            disabled: !resConfig,
            onChange: (v) => updateRes.mutate({ reminderEnabled: v }, notify),
          })}
        </ListItem>
      </List>

      {/* Customer order emails - order-type axis × payment axis (AND-gated). */}
      <BlockTitle>{`${m.customerGroup} · ${m.byOrderType}`}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {typeRow('PICKUP', 'customerEmailEnabled', m.customerPickupTitle)}
        {typeRow('DELIVERY', 'customerEmailEnabled', m.customerDeliveryTitle)}
      </List>
      <BlockTitle>{`${m.customerGroup} · ${m.byPayment}`}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {allPayRow(
          'customerEmailCashEnabled',
          'customerEmailManualEnabled',
          'customerEmailOnlineEnabled',
          m.allMaster,
        )}
        {payRow('customerEmailCashEnabled', m.cash)}
        {payRow('customerEmailManualEnabled', m.manual)}
        {payRow('customerEmailOnlineEnabled', m.online)}
      </List>

      {/* Owner/staff EMAIL for new orders - rides on the app alert (managed in
          Settings → Push notifications), so it is disabled per type when that
          type's owner notify is off. */}
      {featureFlags.ownerEmailNotifications && (
        <>
          <BlockTitle>{`${m.ownerEmailGroup} · ${m.byOrderType}`}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {typeRow(
              'PICKUP',
              'ownerEmailEnabled',
              m.ownerPickupTitle,
              (c) => !(c?.ownerNotifyEnabled ?? true),
            )}
            {typeRow(
              'DELIVERY',
              'ownerEmailEnabled',
              m.ownerDeliveryTitle,
              (c) => !(c?.ownerNotifyEnabled ?? true),
            )}
          </List>
        </>
      )}

      <Block>
        <p className="text-sm text-gray-500">{m.customerNote}</p>
        <p className="text-sm text-gray-500">{m.ownerNote}</p>
      </Block>
    </Page>
  );
}

export default MailSettingsPage;
